import Cookies from 'js-cookie';
import { GoogleAuthProvider, OAuthCredential } from 'firebase/auth';
import {
    ICampaign,
    IEditContactRequest,
    IParsedProAccessResponse,
    ISaveContactRequest,
    ISaveContactResponse,
    TCampaignPage,
    IUserResponse
} from '~/types/server.types';
import { AccountRequestActions } from '~/models/account-access-request.model';
import { trackError } from '~/services/logger/error.utils';
import {
    ConvertPreTrailerData,
    SendResetPasswordUrlParams,
    UserAdapter
} from '~/services/adapters/user.adapter';
import type { JStorageService } from '~/services/j-storage.service';
import type { IApiService } from '~/services/interfaces/IApiService';
import { ProAccountAccessAuthorization } from '~/models/pro-access-request.model';
import { convertCustomFieldsForServer } from '~/components/CustomFields/custom-fields.utils';
import { Contact } from '~/models/contact.model';
import { IChangePasswordByEmailParams } from '~/types/angular.types';
import {
    saveUserToLocalStorage,
    getTrialDataFromQS
} from '~/common/user.utils';
import { IConfigurationProvider } from '~/types/dependency.types';

export class UserService extends UserAdapter {
    configProvider: IConfigurationProvider;

    constructor({
        apiService,
        configProvider
    }: {
        apiService: IApiService;
        configProvider: IConfigurationProvider;
    }) {
        super(apiService);

        this.configProvider = configProvider;
    }

    convertMtaCookieToHeader() {
        const headers = {};
        const mta_uuid = Cookies.get('hb_mta_uuid');
        if (mta_uuid) {
            headers[this.configProvider.config.hb_api_headers.mta_uuid] =
                mta_uuid;
        }
        return headers;
    }

    async loginWithGoogleFirebase(result, credentialByIdToken = false) {
        let credential: OAuthCredential | null = null;
        if (credentialByIdToken) {
            credential = GoogleAuthProvider.credential(result.credential);
        } else {
            credential = GoogleAuthProvider.credentialFromResult(result);
        }

        const { idToken } = credential;
        const google = 'google';

        const data = {
            auth_token: idToken,
            id_token: idToken,
            sso_type: google
        };

        const trialData = getTrialDataFromQS();

        return super._loginWithGoogleFirebase({ ...data, ...trialData });
    }

    async createNewUser({
        userData,
        jStorageService,
        recaptchaToken = ''
    }: {
        userData;
        jStorageService: JStorageService;
        recaptchaToken?: string;
    }) {
        const headers = this.convertMtaCookieToHeader();

        // will be undefined for tests and should be ignored in server
        userData['g-recaptcha-response-v3'] = recaptchaToken;

        const newUserResponse = await super._createNewUser(userData, headers);

        saveUserToLocalStorage({ user: newUserResponse, jStorageService });

        return newUserResponse;
    }

    async editContact(data: IEditContactRequest) {
        const { custom_contact_fields } = data;
        const updatedFields = convertCustomFieldsForServer(
            custom_contact_fields
        );
        const updatedData = { ...data, custom_contact_fields: updatedFields };

        return super.editContact(updatedData);
    }

    async editContactModel(contact: Contact) {
        const { custom_fields, client_id, ...rest } = contact;

        const convertedContact = {
            ...rest,
            _id: client_id,
            full_name: contact.fullName,
            custom_contact_fields: custom_fields
                ?.filter(
                    field => field.value !== null && field.value !== undefined
                )
                .reduce((acc, custom_field) => {
                    acc[custom_field.linked_id] = custom_field.value;
                    return acc;
                }, {})
        };
        return this.editContact(convertedContact);
    }

    async getCampaigns(page: TCampaignPage): Promise<ICampaign[]> {
        return (await super._getCampaigns(page)) || [];
    }

    async getProRequests(requestParams: {
        statuses: string | undefined;
        expires_after?: Date;
        limit?: number;
        skip?: number;
        as_client: boolean;
        include_statuses_count?: boolean;
        auth_type?: 'pro' | 'bookkeeper';
    }): Promise<IParsedProAccessResponse> {
        const serverParams = {
            ...requestParams,
            auth_type: requestParams.auth_type || 'pro'
        };
        if (!serverParams.as_client) {
            serverParams.include_statuses_count = true;
        }
        const presentParams = Object.entries(serverParams)
            .filter(([, value]) => value != undefined)
            .map(([key, value]) => `${key}=${value}`);

        const response = await super._getProRequests(presentParams);

        const requestInstances = response.results.map(request => {
            const requestInstance = new ProAccountAccessAuthorization(request);
            return requestInstance;
        });

        return {
            results: requestInstances,
            statuses_count: response.statuses_count,
            total_results: response.total_results
        };
    }

    async getBookkeeperRequests(asClient: boolean = true) {
        const response = await super._getBookkeeperRequests(asClient);
        return response;
    }

    async proLoginAsVendor({
        userId,
        smsCode,
        jStorageService
    }: {
        userId: string;
        smsCode: string;
        jStorageService: JStorageService;
    }) {
        try {
            const newUser = await super._proLoginAsVendor(userId, smsCode);
            saveUserToLocalStorage({
                user: newUser,
                jStorageService: jStorageService
            });
            sessionStorage.setItem('PRO_LOGIN', 'true');

            // Backup to session storage
            event = new CustomEvent('PRO_LOGIN', {
                detail: {
                    userId: newUser._id
                }
            });

            window.dispatchEvent(event);
            return newUser;
        } catch (error) {
            trackError({
                error,
                message: 'Error logging pro in as vendor',
                context: {
                    vendorId: userId
                }
            });

            throw error;
        }
    }

    async askForAccountAccessExtension(requestId: string) {
        const response = await this.manageProAccountAccess(
            requestId,
            AccountRequestActions.EXTEND
        );
        return response;
    }

    getContacts(email: string, userId: string) {
        return super._getContacts(email, userId);
    }

    async createClientAndAddToWorkspace(
        data: ISaveContactRequest,
        workspace_id: string
    ): Promise<ISaveContactResponse> {
        try {
            const { custom_contact_fields } = data;
            const updatedFields = convertCustomFieldsForServer(
                custom_contact_fields
            );
            const updatedData = {
                ...data,
                custom_contact_fields: updatedFields
            };

            const response = await super.createClientAndAddToWorkspace(
                updatedData,
                workspace_id
            );
            const user = { ...response, company: {} };
            user.notes = user?.contact?.notes;
            return user;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    async addExistingClientToWorkspace(
        email: string,
        workspace_id: string
    ): Promise<ISaveContactResponse> {
        try {
            const response = await super.addExistingClientToWorkspace(
                email,
                workspace_id
            );
            return { ...response, company: {} };
        } catch (error) {
            return Promise.reject(error);
        }
    }

    async addCollaboratorToWorkspace(
        data: ISaveContactRequest,
        workspaceId: string
    ): Promise<ISaveContactResponse> {
        try {
            const response = await super.addCollaboratorToWorkspace(
                data,
                workspaceId
            );
            return { ...response, company: {} };
        } catch (error) {
            return Promise.reject(error);
        }
    }

    async changePasswordByEmail(
        data: IChangePasswordByEmailParams
    ): Promise<IUserResponse> {
        const companyId = (window as any).clientPortalConfiguration?.company_id;
        try {
            return await super.unauthenticatedChangePassword({
                ...data,
                ...(companyId && { company_id: companyId })
            });
        } catch (error) {
            return Promise.reject(error);
        }
    }

    async sendResetPasswordLink(
        email: string,
        params: SendResetPasswordUrlParams = {},
        headers?: Record<string, string>
    ) {
        return super.sendResetPasswordLink(email, params, headers);
    }

    /**
     * Public method to convert a pre-trial user to a trial user.
     * @param userId The ID of the user.
     * @param conversionData The data required for the conversion.
     * @returns Promise resolving with the API response.
     */
    async convertPreTrailerToTrailer(
        userId: string,
        conversionData: ConvertPreTrailerData
    ) {
        try {
            const response = await super._convertPreTrailerToTrailer(
                userId,
                conversionData
            );
            return response;
        } catch (error) {
            trackError({
                error,
                message: 'Error converting pre-trailer user to trailer',
                context: { userId, conversionData }
            });
            throw error;
        }
    }
}
